import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronDown, ChevronUp, Clock, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
interface RMIPageProps {
  onBack: () => void;
  onRequestClick?: (requestId: number) => void;
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
}
type RequestStatus = 'new' | 'pending' | 'in-progress' | 'closed';
type OpenStatus = 'new' | 'pending' | 'in-progress';
interface Request {
  id: number;
  code: string;
  title: string;
  description: string;
  date: string;
  status: RequestStatus;
  developerName: string;
  developerLogo: string;
}
export function RMIPage({
  onBack,
  onRequestClick,
  onNetworkClick,
  onInventoryClick,
  onRMIClick
}: RMIPageProps) {
  const [activeOpenTab, setActiveOpenTab] = useState<OpenStatus>('new');
  const [expandedClosedSection, setExpandedClosedSection] = useState(false);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Mock requests data from all developers
  const [requests] = useState<Request[]>([
  // New Requests
  {
    id: 1,
    code: '762532',
    title: 'Unit Reservation Request',
    description: 'Villa A-105, New Cairo Project',
    date: '2 hours ago',
    status: 'new',
    developerName: 'Mountain View',
    developerLogo: 'https://cdn.magicpatterns.com/uploads/ht2zed5WJfpibcGTs2kKAi/Mountain-View-Logo-Vector.svg-.png'
  }, {
    id: 2,
    code: '891234',
    title: 'Price Inquiry',
    description: 'Apartment B-203, Waterway Project',
    date: '5 hours ago',
    status: 'new',
    developerName: 'Palm Hills',
    developerLogo: 'https://cdn.magicpatterns.com/uploads/e3xetC89TNSJtFzKEK9GJ9/OIP.McQBqob_yPFZovOPZDod3AHaHa.webp'
  }, {
    id: 3,
    code: '345678',
    title: 'Site Visit Request',
    description: 'Duplex Unit, Hyde Park',
    date: '1 day ago',
    status: 'new',
    developerName: 'Hyde Park',
    developerLogo: 'https://cdn.magicpatterns.com/uploads/8vQhTfMrXW8bwweERT8mA7/OIP.xWHw7TazT7rFveGb0a9zNwHaHa.webp'
  },
  // Pending Requests
  {
    id: 4,
    code: '456789',
    title: 'Payment Plan Request',
    description: 'Villa C-301, Emaar Project',
    date: 'Yesterday',
    status: 'pending',
    developerName: 'Emaar Misr',
    developerLogo: 'https://cdn.magicpatterns.com/uploads/wijYmcxqmndw1XGyexVnGN/Emaar-Misr-Logo.png'
  }, {
    id: 5,
    code: '567890',
    title: 'Unit Availability Check',
    description: 'Penthouse, Sodic West',
    date: '2 days ago',
    status: 'pending',
    developerName: 'Sodic',
    developerLogo: 'https://cdn.magicpatterns.com/uploads/m7ECvP64WQu5Q5fDGkVyT2/OIP.heFoNUDluvlfTF84mNzC8AHaB6.webp'
  },
  // In Progress Requests
  {
    id: 6,
    code: '678901',
    title: 'Contract Review',
    description: 'Apartment D-405, Ora Project',
    date: '3 days ago',
    status: 'in-progress',
    developerName: 'Ora Developers',
    developerLogo: 'https://cdn.magicpatterns.com/uploads/qiVDQQJwbWPnXu1qEPHnnJ/1655298523997.jpg'
  }, {
    id: 7,
    code: '789012',
    title: 'Modification Request',
    description: 'Villa Layout Changes, Tatweer Project',
    date: '4 days ago',
    status: 'in-progress',
    developerName: 'Tatweer Misr',
    developerLogo: 'https://cdn.magicpatterns.com/uploads/vFd4y3e6VTvKmbjouHn6Df/OIP.WU0po8LB04ePZzUToFmyKQHaD2.webp'
  }, {
    id: 8,
    code: '890123',
    title: 'Financing Approval',
    description: 'Townhouse Unit, Inertia Project',
    date: '5 days ago',
    status: 'in-progress',
    developerName: 'Inertia',
    developerLogo: 'https://cdn.magicpatterns.com/uploads/gtoHa4YFoGm69rL7tSXhd6/OIP.8_yr1_3JwTWhYPh-6mszrAHaBG.webp'
  },
  // Closed Requests
  {
    id: 9,
    code: '901234',
    title: 'Unit Reservation Completed',
    description: 'Villa E-501, Mountain View',
    date: '1 week ago',
    status: 'closed',
    developerName: 'Mountain View',
    developerLogo: 'https://cdn.magicpatterns.com/uploads/ht2zed5WJfpibcGTs2kKAi/Mountain-View-Logo-Vector.svg-.png'
  }, {
    id: 10,
    code: '012345',
    title: 'Price Inquiry Resolved',
    description: 'Apartment F-602, Palm Hills',
    date: '2 weeks ago',
    status: 'closed',
    developerName: 'Palm Hills',
    developerLogo: 'https://cdn.magicpatterns.com/uploads/e3xetC89TNSJtFzKEK9GJ9/OIP.McQBqob_yPFZovOPZDod3AHaHa.webp'
  }, {
    id: 11,
    code: '123456',
    title: 'Site Visit Completed',
    description: 'Multiple Units, Hyde Park',
    date: '3 weeks ago',
    status: 'closed',
    developerName: 'Hyde Park',
    developerLogo: 'https://cdn.magicpatterns.com/uploads/8vQhTfMrXW8bwweERT8mA7/OIP.xWHw7TazT7rFveGb0a9zNwHaHa.webp'
  }, {
    id: 12,
    code: '234567',
    title: 'Contract Signed',
    description: 'Apartment G-703, Emaar Project',
    date: '1 month ago',
    status: 'closed',
    developerName: 'Emaar Misr',
    developerLogo: 'https://cdn.magicpatterns.com/uploads/wijYmcxqmndw1XGyexVnGN/Emaar-Misr-Logo.png'
  }]);
  const getStatusConfig = (status: RequestStatus) => {
    const configs = {
      new: {
        label: 'New',
        icon: Clock,
        color: 'text-secondary',
        bgColor: 'bg-secondary/10',
        borderColor: 'border-secondary/30'
      },
      pending: {
        label: 'Pending',
        icon: AlertCircle,
        color: 'text-accent-dark',
        bgColor: 'bg-accent/10',
        borderColor: 'border-accent/30'
      },
      'in-progress': {
        label: 'In Progress',
        icon: AlertCircle,
        color: 'text-primary',
        bgColor: 'bg-primary/10',
        borderColor: 'border-primary/30'
      },
      closed: {
        label: 'Closed',
        icon: CheckCircle2,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      }
    };
    return configs[status];
  };
  const handleRequestCardClick = (requestId: number) => {
    if (onRequestClick) {
      onRequestClick(requestId);
    }
  };
  // Group requests by status
  const newRequests = requests.filter(r => r.status === 'new');
  const pendingRequests = requests.filter(r => r.status === 'pending');
  const inProgressRequests = requests.filter(r => r.status === 'in-progress');
  const closedRequests = requests.filter(r => r.status === 'closed');
  const openRequestsCount = newRequests.length + pendingRequests.length + inProgressRequests.length;
  const getCurrentOpenRequests = () => {
    switch (activeOpenTab) {
      case 'new':
        return newRequests;
      case 'pending':
        return pendingRequests;
      case 'in-progress':
        return inProgressRequests;
      default:
        return [];
    }
  };
  const renderRequestCard = (request: Request) => {
    const statusConfig = getStatusConfig(request.status);
    const StatusIcon = statusConfig.icon;
    return <button key={request.id} onClick={() => handleRequestCardClick(request.id)} className={`w-full bg-white rounded-lg border ${statusConfig.borderColor} p-4 shadow-sm hover:shadow-md transition-shadow text-left`} data-id="element-1811">
        <div className="flex items-start gap-3 mb-3" data-id="element-1812">
          {/* Developer Logo */}
          <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 p-1.5 flex items-center justify-center flex-shrink-0" data-id="element-1813">
            <img src={request.developerLogo} alt={request.developerName} className="w-full h-full object-contain" data-id="element-1814" />
          </div>

          {/* Request Info */}
          <div className="flex-1 min-w-0" data-id="element-1815">
            <div className="flex items-start justify-between mb-1" data-id="element-1816">
              <p className="text-xs font-semibold text-secondary" data-id="element-1817">
                Request No. #{request.code}
              </p>
              <span className={`flex items-center gap-1 ${statusConfig.bgColor} ${statusConfig.color} text-xs font-semibold px-2 py-1 rounded-full ml-2 flex-shrink-0`} data-id="element-1818">
                <StatusIcon className="w-3 h-3" data-id="element-1819" />
                {statusConfig.label}
              </span>
            </div>
            <h4 className="font-semibold text-neutral-text text-sm mb-1" data-id="element-1820">
              {request.title}
            </h4>
            <p className="text-xs text-gray-500 mb-1" data-id="element-1821">
              {request.developerName}
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-2" data-id="element-1822">{request.description}</p>
        <p className="text-xs text-gray-400" data-id="element-1823">{request.date}</p>
      </button>;
  };
  return <div className="min-h-screen bg-neutral-bg pb-20 font-sans w-full" data-id="element-1824">
      <div className="w-full mx-auto bg-neutral-bg min-h-screen relative" data-id="element-1825">
        {/* Header */}
        <div className="bg-primary px-4 py-4 safe-top flex items-center justify-between sticky top-0 z-10 shadow-sm" data-id="element-1826">
          <button onClick={onBack} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-1827">
            <ChevronLeft className="w-6 h-6" data-id="element-1828" />
          </button>
          <h1 className="font-semibold text-lg text-white" data-id="element-1829">
            RMI - All Requests
          </h1>
          <div className="w-10" data-id="element-1830"></div>
        </div>

        {/* Summary Cards */}
        <div className="px-4 pt-4 pb-4" data-id="element-1831">
          <div className="grid grid-cols-2 gap-3" data-id="element-1832">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-1833">
              <p className="text-xs text-gray-500 mb-1" data-id="element-1834">Open Requests</p>
              <p className="text-2xl font-bold text-secondary" data-id="element-1835">
                {openRequestsCount}
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-1836">
              <p className="text-xs text-gray-500 mb-1" data-id="element-1837">Closed Requests</p>
              <p className="text-2xl font-bold text-green-600" data-id="element-1838">
                {closedRequests.length}
              </p>
            </div>
          </div>
        </div>

        {/* Open Status Section */}
        <div className="px-4 pb-4" data-id="element-1839">
          <h2 className="text-base font-bold text-neutral-text mb-3" data-id="element-1840">
            Open Requests
          </h2>

          {/* Rounded Tabs for Open Status */}
          <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar" data-id="element-1841">
            <button onClick={() => setActiveOpenTab('new')} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${activeOpenTab === 'new' ? 'bg-secondary text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'}`} data-id="element-1842">
              New ({newRequests.length})
            </button>
            <button onClick={() => setActiveOpenTab('pending')} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${activeOpenTab === 'pending' ? 'bg-accent text-primary' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'}`} data-id="element-1843">
              Pending ({pendingRequests.length})
            </button>
            <button onClick={() => setActiveOpenTab('in-progress')} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${activeOpenTab === 'in-progress' ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'}`} data-id="element-1844">
              In Progress ({inProgressRequests.length})
            </button>
          </div>

          {/* Open Requests List */}
          <div className="space-y-3" data-id="element-1845">
            {getCurrentOpenRequests().map(request => renderRequestCard(request))}
          </div>
        </div>

        {/* Closed Status Section */}
        <div className="px-4 pb-4" data-id="element-1846">
          <button onClick={() => setExpandedClosedSection(!expandedClosedSection)} className="w-full flex items-center justify-between mb-3 px-2 py-1 hover:bg-white/50 rounded-lg transition-colors" data-id="element-1847">
            <div className="flex items-center gap-2" data-id="element-1848">
              <h2 className="text-base font-bold text-neutral-text" data-id="element-1849">
                Closed Requests
              </h2>
              <span className="bg-green-100 text-green-600 text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-1850">
                {closedRequests.length}
              </span>
            </div>
            {expandedClosedSection ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-1851" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-1852" />}
          </button>

          {expandedClosedSection && <div className="space-y-3" data-id="element-1853">
              {closedRequests.map(request => renderRequestCard(request))}
            </div>}
        </div>

        <BottomNav onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} activeNav="rmi" data-id="element-1854" />
      </div>
    </div>;
}